/**
 * Post-Filter Rejections Chart Component
 *
 * Area chart showing post-filter document rejections over time.
 * These are documents rejected by the defense-in-depth client filter
 * after Bedrock's metadata filtering. Should be ~0 in a healthy system.
 */

import { useId, useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { formatChartTime, formatCompactNumber } from '@/lib/formatters';
import { ChartCard, CHART_HEIGHT_CLASSES } from './ChartCard';
import {
  createDotProps,
  createActiveDotProps,
  createGradientIds,
} from './chart-utils';
import { MetricTooltip } from './MetricTooltip';
import { METRIC_DESCRIPTIONS } from './MetricDescriptions';
import type { DataPoint } from '@/types/dashboard';

interface PostFilterRejectionsChartProps {
  data: DataPoint[] | undefined;
  isLoading: boolean;
  rejectionRate?: number;
}

interface ChartPoint {
  time: string;
  timestamp: string;
  rejections: number;
}

function formatData(data: DataPoint[] | undefined): ChartPoint[] {
  if (!data) return [];

  return data
    .map((point) => ({
      time: formatChartTime(point.timestamp),
      timestamp: point.timestamp,
      rejections: Math.round(point.value),
    }))
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
}

const chartConfig = {
  rejections: {
    label: 'Rejections',
    color: 'hsl(var(--destructive))',
  },
} satisfies ChartConfig;

export function PostFilterRejectionsChart({
  data,
  isLoading,
  rejectionRate,
}: PostFilterRejectionsChartProps) {
  const chartId = useId();
  const gradientIds = useMemo(
    () => createGradientIds(chartId, ['fillRejections']),
    [chartId],
  );

  const chartData = useMemo(() => formatData(data), [data]);

  // Check if there's any data with rejections
  const hasData = chartData.some((d) => d.rejections > 0);

  const showSkeleton = isLoading || data === undefined;
  const isEmpty = !showSkeleton && (chartData.length === 0 || !hasData);

  // Calculate total rejections for display
  const totalRejections = useMemo(
    () => chartData.reduce((sum, point) => sum + point.rejections, 0),
    [chartData],
  );

  // Header shows rejection rate (percentage) and total count
  const headerContent = rejectionRate !== undefined && (
    <div className="flex gap-4 text-sm text-muted-foreground">
      <MetricTooltip
        label="Rate"
        value={`${Math.round(rejectionRate * 100)}%`}
        tooltip={METRIC_DESCRIPTIONS.postFilterRejectionRate}
      />
      {totalRejections > 0 && (
        <span className="text-destructive">
          <MetricTooltip
            label="Total"
            value={formatCompactNumber(totalRejections)}
            tooltip={METRIC_DESCRIPTIONS.totalRejections}
          />
        </span>
      )}
    </div>
  );

  return (
    <ChartCard
      title="Post-Filter Rejections"
      tooltip={METRIC_DESCRIPTIONS.postFilterRejections}
      headerContent={headerContent}
      isLoading={showSkeleton}
      isEmpty={isEmpty}
      emptyMessage="No post-filter rejections in the selected time range"
      emptyAriaLabel="No post-filter rejection data available"
    >
      <ChartContainer
        config={chartConfig}
        className={`${CHART_HEIGHT_CLASSES} w-full`}
      >
        <AreaChart data={chartData} accessibilityLayer>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="time"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            allowDecimals={false}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <defs>
            <linearGradient
              id={gradientIds.fillRejections}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor="var(--color-rejections)"
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor="var(--color-rejections)"
                stopOpacity={0.1}
              />
            </linearGradient>
          </defs>
          <Area
            dataKey="rejections"
            name="Rejections"
            type="monotone"
            fill={`url(#${gradientIds.fillRejections})`}
            fillOpacity={0.4}
            stroke="var(--color-rejections)"
            strokeWidth={2}
            dot={createDotProps(chartData.length, 'var(--color-rejections)')}
            activeDot={createActiveDotProps(
              chartData.length,
              'var(--color-rejections)',
            )}
          />
        </AreaChart>
      </ChartContainer>
    </ChartCard>
  );
}
